import { type ReactNode, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { useRouteLoaderData, useSearchParams } from '@remix-run/react';
import type { Recommendation } from '@shared/dream-components';

import { usePageContext } from '~/context/usePageContext';
import useEmail from '~/hooks/useEmail';
import type { loader } from '~/root';
import { trackRecommendationClicks } from '~/services/api/recommendations/trackClicks';
import toast from '~/utils/toast';

import ActionForm from '../ActionForm';
import { useComponentFetcher } from '../ComponentErrorBoundary/useComponentFetcher';
import StatusInputs from '../Forms/StatusInputs';
import useFormSubmit from './_hooks/useFormSubmit';

type RenderProps = {
  isLoading: boolean;
  email?: string;
};

type Props = {
  children: (props: RenderProps) => ReactNode;
  recommendations: Recommendation[];
  triggerSource?: 'individual_card_button' | 'subscribe_all_button';
  onSubmitSuccess?: (recommendations: Recommendation[]) => void;
};

const RecommendationsModalForm = ({
  recommendations,
  triggerSource = 'individual_card_button',
  children,
  onSubmitSuccess,
}: Props) => {
  const { t } = useTranslation();
  const [email] = useEmail();
  const fetcher = useComponentFetcher();
  const { site } = useRouteLoaderData<typeof loader>('root');
  const publication = site.publication;
  const { visitToken, referralId, setCookie } = usePageContext();
  const [searchParams] = useSearchParams();
  const subscriberId = searchParams.get('subscriber_id') ?? undefined;
  const subscriptionUuid = searchParams.get('sid') ?? undefined;
  const recommendationWidgetId = searchParams.get('recommendation_widget_id') ?? undefined;

  const formRef = useRef<HTMLFormElement>(null);

  const { handleSubmit } = useFormSubmit({ fetcher });

  const handleSuccess = () => {
    setCookie({ name: 'email_capture_disabled', value: 'true', days: 30 });

    toast.success(t('notifications.subscribe.subscribed'));
    onSubmitSuccess?.(recommendations);
    formRef.current?.reset();
  };

  const handleError = (errPayload: any) => {
    toast.success(errPayload?.response?.data?.error || t('errors.default'));
  };

  const isLoading = fetcher.state === 'submitting';

  return (
    <ActionForm
      fetcher={fetcher}
      formRef={formRef}
      method="post"
      action="/recommendations_subscribe_form"
      onSubmit={(e) => {
        const formData = new FormData(e.currentTarget);
        trackRecommendationClicks(recommendations, subscriberId, subscriptionUuid, 'subscribe', triggerSource).catch(
          () => null,
        );
        handleSubmit(formData);
      }}
      onSuccess={handleSuccess}
      onError={handleError}
    >
      <StatusInputs name="recommendations" errorMessage={t('errors.default')} successMessage="" />
      {referralId && <input hidden name="ref" defaultValue={referralId} />}
      {visitToken && <input hidden name="visit_token" defaultValue={visitToken} />}
      <input hidden name="publication_id" defaultValue={publication.id} />
      {recommendationWidgetId && <input hidden name="recommendation_widget_id" defaultValue={recommendationWidgetId} />}
      {email && <input hidden name="email" defaultValue={email} />}
      <input name="sent_from_orchid" type="hidden" defaultValue="true" />
      <input name="include_current_publication" type="hidden" defaultValue="false" />
      {recommendations.map((rec) => (
        <input hidden key={rec.id} name="recommendation_ids[]" defaultValue={rec.id} />
      ))}
      {children({
        isLoading,
        email,
      })}
    </ActionForm>
  );
};

export default RecommendationsModalForm;
